import React, { useState } from 'react';
import { Header } from './components/Header';
import { UrlForm } from './components/UrlForm';
import { AudioPlayer } from './components/AudioPlayer';
import { ErrorMessage } from './components/ErrorMessage';
import { ProcessExplanation } from './components/ProcessExplanation';
import { HelpModal } from './components/HelpModal';
import { processUrl } from './services/urlProcessor';
import type { Language } from './translations';

export default function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [script, setScript] = useState('');
  const [darkMode, setDarkMode] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [language, setLanguage] = useState<Language>('nl');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setAudioUrl('');
    setScript('');

    try {
      const result = await processUrl(url, language);
      setAudioUrl(result.audioUrl);
      setScript(result.dialogue);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900 text-white' : 'bg-orange-50 text-black'
    }`}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Header 
          darkMode={darkMode} 
          onToggleDarkMode={() => setDarkMode(!darkMode)}
          onOpenHelp={() => setIsHelpOpen(true)}
          language={language}
          onLanguageChange={setLanguage}
        />
        
        <div className={`p-6 rounded-2xl shadow-lg ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-orange-200'
        } transition-all duration-300`}>
          <UrlForm
            url={url}
            loading={loading}
            darkMode={darkMode}
            language={language}
            onUrlChange={setUrl}
            onSubmit={handleSubmit}
          />
          
          <ErrorMessage message={error} language={language} />
          
          <ProcessExplanation 
            loading={loading} 
            language={language}
          />
          
          <AudioPlayer 
            audioUrl={audioUrl} 
            script={script} 
            darkMode={darkMode}
            language={language}
          />
        </div>

        <footer className="mt-8 text-center space-y-4">
          <p className="text-orange-500 font-medium">
            © {new Date().getFullYear()} P3Media - AI Podcast Generator
          </p>
          <p className="text-orange-500/80">
            Een innovatief project van{' '}
            <a 
              href="https://www.p3media.be" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-orange-500 hover:text-orange-600 transition-colors font-medium"
            >
              P3Media
            </a>
          </p>
        </footer>
      </div>

      <HelpModal 
        isOpen={isHelpOpen} 
        onClose={() => setIsHelpOpen(false)} 
        language={language}
      />
    </div>
  );
}